import {
  Amenity as AmenityType,
  Belonging as BelongingType,
  FacilityDetails,
  Gender,
  ProgramDetails,
  ProgramId,
  ProgramLevel,
  TimetableId,
} from '@gyym/sdk'
import {
  AutoLink,
  Badge,
  CalendarDateSelect,
  Carousel,
  Empty,
  Image,
  levelLabelMap,
  Lightbox,
  Link,
  Loading,
  ReadMore,
  ReviewList,
  SwiperStyles,
  TimetableList,
  Typography,
  useSnackbar,
  useTheme,
} from '@gyym/ui'
import {
  Amenity,
  Belonging,
  Clock,
  ErrorOutline,
  Facility,
  LocationOutline,
  Point,
} from '@gyym/ui/icons'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import React from 'react'

import Map from '@/components/UI/Map'
import { useAuthUser } from '@/modules/Auth'
import {
  buildFacilityLink,
  useCanRequestReservation,
  useTimetableReserve,
} from '@/modules/Facilities'
import { buildProgramLink, useProgramTimetables } from '@/modules/Programs'
import { genderLabels } from '@/modules/Users'
import { formatNumber } from '@/utils/formatNumber'
import { getImageUrl } from '@/utils/getImageUrl'
import { nl2br } from '@/utils/nl2br'

import {
  BleedContent,
  BorderTopFlex,
  BulletedList,
  BulletedListItem,
  Container,
  TopContent,
} from './styles'

const getMapQuery = (facility: FacilityDetails): string => {
  // Use latitude and longitude if available
  if (facility.latitude && facility.longitude) {
    return facility.latitude + ',' + facility.longitude
  }
  // Use address if available
  // The building name is expected to be separated by spaces after the address
  return facility.address.split(' ')[0] ?? ''
}

const getMapLink = (facility: FacilityDetails): string =>
  `https://maps.google.co.jp/maps?z=16&q=${getMapQuery(facility)}`

const belongings: { key: keyof BelongingType; label: string }[] = [
  {
    key: 'wear',
    label: 'ウェア',
  },
  {
    key: 'shoes',
    label: 'シューズ',
  },
  {
    key: 'socks',
    label: '靴下',
  },
  {
    key: 'towel',
    label: 'タオル',
  },
  {
    key: 'drink',
    label: 'ドリンク',
  },
]

const amenities: { key: keyof AmenityType; label: string }[] = [
  {
    key: 'locker',
    label: 'ロッカー',
  },
  {
    key: 'shower',
    label: 'シャワー',
  },
  {
    key: 'powderRoom',
    label: '化粧室',
  },
  {
    key: 'dryer',
    label: 'ドライヤー',
  },
  {
    key: 'shampoo',
    label: 'シャンプー',
  },
  {
    key: 'lotion',
    label: '化粧水',
  },
  {
    key: 'spa',
    label: 'スパ',
  },
  {
    key: 'sauna',
    label: 'サウナ',
  },
  {
    key: 'coldBath',
    label: '水風呂',
  },
]

export interface ProgramDetailViewProps {
  program: ProgramDetails
}

export const ProgramDetailView = ({ program }: ProgramDetailViewProps) => {
  const facility = program.facility

  const router = useRouter()
  const { isLoggedIn, authUser } = useAuthUser()
  const { openSnackbar } = useSnackbar()
  const {
    data: timetables = [],
    mutate: mutateTimetables,
    isValidating: isTimetablesValidating,
  } = useProgramTimetables(program.id, { revalidateOnMount: true })

  const { loadingTimetableId, openReservationModal } = useTimetableReserve(
    facility,
    'facility',
    React.useCallback(
      async (timetableId: TimetableId) => {
        await mutateTimetables(
          timetables.map((t) =>
            t.id === timetableId
              ? {
                  ...t,
                  reserved: true,
                }
              : t,
          ),
          { revalidate: true },
        )
        return
      },
      [timetables, mutateTimetables],
    ),
    // The modal can open for a different program via the URL query, in which
    // case this page's image would be wrong — fall back to the OGP image
    React.useCallback(
      (programId: ProgramId) =>
        programId === program.id ? program.image : undefined,
      [program.id, program.image],
    ),
  )

  const { data: { canRequest } = { canRequest: false } } =
    useCanRequestReservation({ revalidateOnMount: true })

  const timetablesWithProgram = timetables.map((t) => {
    return {
      ...t,
      programId: program?.id ?? 0,
      programName: program?.name ?? '',
      programDuration: program?.duration ?? 0,
      programActivities: program?.activities ?? [],
      programLevel: program?.level ?? ProgramLevel.ENTRY,
      gender: program?.sexLimitation ?? Gender.NONE,
    }
  })

  // Group timetables by startAt
  const timetablesByStartAt = timetablesWithProgram.reduce((acc, timetable) => {
    const startAt = dayjs(timetable.startAt).format('YYYY-MM-DD')

    return {
      ...acc,
      [startAt]: [...(acc[startAt] ?? []), timetable],
    }
  }, {} as Record<string, typeof timetablesWithProgram>)

  // List of available dates
  const timetableStartAtDates = Object.keys(timetablesByStartAt)
  // First available date
  const firstTimetableDate = timetableStartAtDates[0]

  const [currentTimetableDate, setCurrentTimetableDate] =
    React.useState(firstTimetableDate)

  const currentTimetables = currentTimetableDate
    ? timetablesByStartAt[currentTimetableDate] ?? []
    : []

  // Switch to the first available date when the timetable gets filtered
  React.useEffect(() => {
    setCurrentTimetableDate(firstTimetableDate)
  }, [firstTimetableDate])

  const theme = useTheme()
  const red01 = theme.colors.red['01']

  const [lightboxState, setLightboxState] = React.useState<{
    open: boolean
    index: number
  }>({
    open: false,
    index: 0,
  })

  const facilityLink = buildFacilityLink(facility)

  return (
    <>
      <AutoLink.GlobalStyles />

      <Container>
        <BleedContent>
          <Carousel
            infinite
            images={[program.image].map((image, i) => ({
              src: getImageUrl(image),
              alt: `${program.name}の画像 ${i + 1}`,
              fill: true,
              sizes: '100%',
              style: { objectFit: 'cover' },
            }))}
          />

          <TopContent>
            <div className="mt-6 flex justify-center">
              <Typography variant="headline1" as="h1">
                {program.name}
              </Typography>
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
              {program.activities.map((activity) => (
                <Badge key={activity.id} variant="default">
                  {activity.name}
                </Badge>
              ))}

              {program.level && (
                <Badge variant="default">{levelLabelMap[program.level]}</Badge>
              )}

              {program.sexLimitation === Gender.MEN && (
                <Badge variant="men">男性専用</Badge>
              )}

              {program.sexLimitation === Gender.WOMEN && (
                <Badge variant="women">女性専用</Badge>
              )}
            </div>

            <div className="mt-6 flex flex-col">
              <div className="flex">
                <Link {...facilityLink}>
                  <div className="flex items-center">
                    <Facility gradient />
                    <Typography variant="subhead2" ml={2} color="gray.01">
                      {facility.name}
                    </Typography>
                  </div>
                </Link>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <div className="flex items-center">
                  <Point />
                  <Typography color="gray.01" ml={2}>
                    {formatNumber(program.minTimetablePoint)}pt〜
                  </Typography>
                </div>
                <div className="flex items-center">
                  <Clock />
                  <Typography color="gray.01" ml={2}>
                    {program.duration}min
                  </Typography>
                </div>
                <div className="flex items-center">
                  <LocationOutline />
                  <Typography color="gray.01" ml={2}>
                    {facility.distance}
                  </Typography>
                </div>
              </div>
            </div>

            <BorderTopFlex className="mt-4 flex-col pt-4">
              <ReadMore fixedButton>
                <AutoLink>{nl2br(program.description)}</AutoLink>
              </ReadMore>
            </BorderTopFlex>
          </TopContent>
        </BleedContent>

        <div className="mt-[60px] flex flex-col">
          <div className="flex items-center">
            <ErrorOutline fill={red01} />

            {program.minuteAgo > 0 ? (
              <Typography as="h2" color="gray.01" variant="subhead1" ml={2}>
                受講プログラムの開始{program.minuteAgo}分前までのご来店
              </Typography>
            ) : (
              <Typography as="h2" color="gray.01" variant="subhead1" ml={2}>
                受講プログラムの開始時間までにご来店
              </Typography>
            )}
          </div>

          <Typography variant="body1" color="gray.01" mt={2}>
            着替え等の事前準備にかかる時間があります。プログラム開始に遅れないよう、上記時間までにご来店ください。
          </Typography>
        </div>

        <Typography id="amenities" variant="headline2" as="h2" mt={15}>
          持ち物
        </Typography>
        <Typography variant="caption1" as="div">
          Preparation
        </Typography>

        <div className="mt-6 flex flex-wrap gap-2 tablet:gap-3">
          {belongings.map(({ key, label }) => (
            <div
              key={key}
              className="flex w-[65px] flex-col items-center gap-1"
            >
              <Belonging type={key} active={program.belongings[key]} />
              <Typography variant="caption1">{label}</Typography>
            </div>
          ))}
        </div>

        <BulletedList>
          {program.otherBelongings.map((otherBelonging, i) => (
            <BulletedListItem key={i}>
              <Typography variant="body1" color="gray.01" as="span">
                <AutoLink>{otherBelonging}</AutoLink>
              </Typography>
            </BulletedListItem>
          ))}
        </BulletedList>

        <Typography id="amenities" variant="headline2" as="h2" mt={15}>
          設備・アメニティ
        </Typography>
        <Typography variant="caption1" as="div">
          Amenities
        </Typography>

        <div className="mt-6 flex flex-wrap gap-2 tablet:gap-3">
          {amenities.map(({ key, label }) => (
            <div
              key={key}
              className="flex w-[65px] flex-col items-center gap-1"
            >
              <Amenity type={key} active={program.amenities[key]} />
              <Typography variant="caption1">{label}</Typography>
            </div>
          ))}
        </div>

        {program.attention.length + program.facilityAttention.length > 0 && (
          <>
            <Typography id="attention" variant="headline2" as="h2" mt={15}>
              注意事項
            </Typography>
            <Typography variant="caption1" as="div">
              Attention
            </Typography>

            <BulletedList>
              {program.attention.map((attention, i) => (
                <BulletedListItem key={`program-${i}`}>
                  <Typography variant="body1" color="gray.01" as="span">
                    <AutoLink>{attention}</AutoLink>
                  </Typography>
                </BulletedListItem>
              ))}
              {program.facilityAttention.map((attention, i) => (
                <BulletedListItem key={`facility-${i}`}>
                  <Typography variant="body1" color="gray.01" as="span">
                    <AutoLink>{attention}</AutoLink>
                  </Typography>
                </BulletedListItem>
              ))}
            </BulletedList>
          </>
        )}

        <div className="mt-16 flex flex-col">
          <div style={{ overflowX: 'auto' }}>
            <div className="flex gap-1 pb-3">
              {program.facilityImages.map((image, i) => (
                <Image
                  key={i}
                  alt={`施設画像 ${i + 1}`}
                  src={getImageUrl(image)}
                  height={100}
                  width={100}
                  sizes="200px"
                  style={{ objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => setLightboxState({ open: true, index: i })}
                />
              ))}
              <Lightbox
                {...lightboxState}
                onClose={() =>
                  setLightboxState((prev) => ({ ...prev, open: false }))
                }
                images={program.facilityImages.map((image, i) => ({
                  src: getImageUrl(image),
                  alt: `施設画像 ${i + 1}`,
                  fill: true,
                  sizes: '100%',
                }))}
                infinite
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Link variant="secondary" {...facilityLink}>
              施設詳細を見る
            </Link>
          </div>
        </div>

        <Typography id="review" variant="headline2" as="h2" mt={15}>
          施設レビュー
        </Typography>
        <Typography variant="caption1" as="div">
          Review
        </Typography>

        <div className="mt-6 flex flex-col gap-6">
          {program.reviews.length > 0 && (
            <ReviewList
              reviews={program.reviews}
              moreLink={
                program.facilityReviewCount > 1
                  ? {
                      href: `${facilityLink.as}/reviews`,
                      external: true,
                    }
                  : undefined
              }
            />
          )}

          {program.reviews.length === 0 && (
            <Empty>この施設にはまだレビューがありません</Empty>
          )}
        </div>

        <Typography id="access" variant="headline2" as="h2" mt={15}>
          アクセス
        </Typography>
        <Typography variant="caption1" as="div">
          Access
        </Typography>

        <div className="mt-6 flex flex-col gap-4">
          {facility.latitude && facility.longitude && (
            <div style={{ position: 'relative', height: 400, width: '100%' }}>
              <Map
                center={{
                  lat: facility.latitude,
                  lng: facility.longitude,
                }}
                locations={[
                  {
                    id: facility.id,
                    lat: facility.latitude,
                    lng: facility.longitude,
                  },
                ]}
                mapOptions={{
                  zoom: 16,
                  zoomControl: true,
                }}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Typography variant="subhead1" as="h3">
              {facility.address}
            </Typography>
            <Typography variant="body2">{facility.distance}</Typography>
            <Typography variant="body2" as="p" wordBreak="break-all">
              <AutoLink>{nl2br(facility.access)}</AutoLink>
            </Typography>
            <div className="flex justify-end">
              <Link
                variant="secondary"
                href={getMapLink(facility)}
                external
                target="_blank"
                rel="noopener noreferrer"
              >
                マップを表示
              </Link>
            </div>
          </div>
        </div>

        <Typography id="schedule" variant="headline2" as="h2" mt={15}>
          スケジュール
        </Typography>
        <Typography variant="caption1" as="div">
          Schedule
        </Typography>

        {isTimetablesValidating && <Loading size="md" />}

        {!isTimetablesValidating && timetables.length === 0 && (
          <Empty mt={6}>現在ご利用いただけるスケジュールがありません</Empty>
        )}

        {!isTimetablesValidating && timetables.length > 0 && (
          <div className="mt-8 flex flex-col gap-3">
            <div className="flex">
              <SwiperStyles />
              <CalendarDateSelect
                id="timetable-dates"
                value={currentTimetableDate}
                options={timetableStartAtDates}
                onChange={setCurrentTimetableDate}
              />
            </div>

            <TimetableList
              timetables={currentTimetables}
              canRequestReservation={canRequest}
              loadingTimetableId={loadingTimetableId}
              onSelect={(timetable) => {
                if (
                  // 性別専用のタイムテーブルで、
                  // 性別が未設定のユーザー、または性別が異なるユーザーは予約できない
                  timetable.gender !== 'none' &&
                  (authUser?.sex === 'none' ||
                    authUser?.sex !== timetable.gender)
                ) {
                  openSnackbar({
                    message: '予約できません',
                    extra: `このプログラムは${
                      genderLabels[timetable.gender]
                    }専用のプログラムになり、${
                      genderLabels[timetable.gender]
                    }の方のみご予約できます。`,
                    variant: 'error',
                  })
                  return
                }

                if (timetable.isApprovable) {
                  const approvableUrl = `${
                    buildProgramLink(facility, {
                      id: timetable.programId,
                    }).as
                  }/reservation_request/${timetable.id}`

                  if (!isLoggedIn) {
                    openSnackbar({
                      message: '仮予約するにはログインが必要です',
                      action: {
                        label: 'ログインする',
                        href: `/login?path=${approvableUrl}`,
                      },
                    })
                    return
                  } else {
                    router.push(approvableUrl)
                  }
                } else {
                  openReservationModal(timetable.programId, timetable.id)
                }
              }}
            />

            <Typography
              variant="body2"
              color="gray.02"
              mt={1 /* + gap3 */}
              px={4}
            >
              ※Instr = インストラクターの略
            </Typography>
          </div>
        )}
      </Container>
    </>
  )
}
